import { z } from 'zod';
import { optionnel, DATE_ISO } from '@shared/validation/helpers';
import { compteOhada } from '@shared/validation/identifiantsGabon';

export const FactureLigneSchema = z.object({
  description: z.string().min(1, 'Description requise'),
  quantite: z.number().positive('Quantité doit être positive'),
  prix_unitaire: z.number().nonnegative('Prix unitaire ≥ 0'),
  taux_tva: z.number().min(0).max(1, 'Taux TVA entre 0 et 1'),
  compte_produit: compteOhada,
  axe_cdc: optionnel(z.string()),
});

export const FactureInputSchema = z
  .object({
    client_id: z.string().min(1, 'Client requis'),
    date: z.string().regex(DATE_ISO, 'Date YYYY-MM-DD requise'),
    date_echeance: z.string().regex(DATE_ISO, 'Échéance YYYY-MM-DD requise'),
    objet: optionnel(z.string()),
    devise: optionnel(z.enum(['XAF', 'EUR', 'USD'])),
    axe_cdc: optionnel(z.string()),
    lignes: z.array(FactureLigneSchema).min(1, 'Au moins une ligne requise'),
  })
  .superRefine((data, ctx) => {
    if (data.date_echeance < data.date) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['date_echeance'],
        message: 'L’échéance précède la date de facture',
      });
    }
  });

/** Formulaire plat (une ligne) monté dans les vues Ventes / Achats. */
export const FactureClientFormSchema = z
  .object({
    numero: z.string().min(1, 'Numéro requis').max(50),
    date: z.string().regex(DATE_ISO, 'Date YYYY-MM-DD requise'),
    date_echeance: z.string().regex(DATE_ISO, 'Échéance YYYY-MM-DD requise'),
    client_id: z.string().min(1, 'Client requis'),
    client_compte: compteOhada,
    description: z.string().min(1, 'Description requise'),
    quantite: z.number().positive('Quantité doit être positive'),
    prix_unitaire: z.number().nonnegative('Prix unitaire ≥ 0'),
    taux_tva: z.number().min(0).max(1, 'Taux TVA entre 0 et 1'),
    compte_produit: compteOhada,
  })
  .superRefine((data, ctx) => {
    if (data.date_echeance < data.date) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['date_echeance'],
        message: 'L’échéance précède la date de facture',
      });
    }
  });

export const FactureFournisseurFormSchema = z
  .object({
    numero: z.string().min(1, 'Numéro requis').max(50),
    date_facture: z.string().regex(DATE_ISO, 'Date YYYY-MM-DD requise'),
    date_echeance: z.string().regex(DATE_ISO, 'Échéance YYYY-MM-DD requise'),
    fournisseur_id: z.string().min(1, 'Fournisseur requis'),
    fournisseur_compte: compteOhada,
    description: z.string().min(1, 'Description requise'),
    quantite: z.number().positive('Quantité doit être positive'),
    prix_unitaire: z.number().nonnegative('Prix unitaire ≥ 0'),
    taux_tva: z.number().min(0).max(1, 'Taux TVA entre 0 et 1'),
    compte_charge: compteOhada,
    precompte_taux: optionnel(z.number().min(0).max(1)),
  })
  .superRefine((data, ctx) => {
    if (data.date_echeance < data.date_facture) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['date_echeance'],
        message: 'L’échéance précède la date de facture',
      });
    }
  });

export type FactureInput = z.infer<typeof FactureInputSchema>;
export type FactureClientFormInput = z.infer<typeof FactureClientFormSchema>;
export type FactureFournisseurFormInput = z.infer<typeof FactureFournisseurFormSchema>;
